// Product page extras: sale timer, bundles, before/after slider, mini reviews.

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!customElements.get('dy-sale-timer')) {
  customElements.define('dy-sale-timer', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.daily !== undefined) {
        const sa = new Date(Date.now() + 2 * 3600e3);
        this.end = Date.UTC(sa.getUTCFullYear(), sa.getUTCMonth(), sa.getUTCDate() + 1) - 2 * 3600e3;
      } else {
        const [d, t = '23:59'] = (this.dataset.end || '').trim().split(/\s+/);
        this.end = new Date(`${d}T${t.length === 5 ? t + ':00' : t}+02:00`).getTime();
      }
      if (!this.end || this.end <= Date.now()) return;
      this.clock = this.querySelector('[data-clock]');
      this.hidden = false;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const left = Math.max(0, this.end - Date.now());
      if (!left) { this.hidden = true; clearInterval(this.timer); return; }
      const s = Math.floor(left / 1000);
      const days = Math.floor(s / 86400);
      const pad = (n) => String(n).padStart(2, '0');
      const hms = `${pad(Math.floor((s % 86400) / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
      this.clock.textContent = days ? `${days}d ${hms}` : hms;
    }
  });
}

function formatMoney(cents, format) {
  const n = Number(cents) / 100;
  const fmt = (dec, thou, sep) => {
    const [i, f] = n.toFixed(dec).split('.');
    return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (dec ? sep + f : '');
  };
  return (format || 'R {{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
    amount: fmt(2, ',', '.'),
    amount_no_decimals: fmt(0, ',', '.'),
    amount_with_comma_separator: fmt(2, '.', ','),
    amount_no_decimals_with_comma_separator: fmt(0, '.', ','),
    amount_with_space_separator: fmt(2, ' ', ','),
    amount_no_decimals_with_space_separator: fmt(0, ' ', ','),
    amount_with_apostrophe_separator: fmt(2, "'", '.'),
  }[k] ?? fmt(2, ',', '.')));
}

if (!customElements.get('dy-bundles')) {
  customElements.define('dy-bundles', class extends HTMLElement {
    connectedCallback() {
      this.fmt = this.dataset.moneyFormat;
      this.addon = this.querySelector('[data-addon-input]');
      this.addEventListener('change', (e) => {
        // Ticking the pouch add-on selects the single-backpack option it belongs to.
        if (e.target === this.addon && this.addon.checked) {
          const first = this.querySelector('[data-option] input[name^="dy-bundle-"]');
          if (first && !first.checked) first.checked = true;
        }
        this.update();
      });
      this.querySelector('[data-atc]').addEventListener('click', () => this.add());
      this.update();
    }
    get selected() { return this.querySelector('.dy-bundles__list input[name^="dy-bundle-"]:checked'); }
    pouchItems() {
      const r = this.selected;
      const wrap = r.closest('[data-option]');
      const pick = (box) => box && (box.querySelector('input[type=radio]:checked') || box.querySelector('input[type=hidden]'));
      const items = [];
      const free = Number(r.dataset.pouches) || 0;
      const freeBox = wrap.querySelector('[data-colours]:not([data-addon-colours])');
      if (free && pick(freeBox)) items.push({ id: Number(pick(freeBox).value), quantity: free });
      if (this.addon?.checked) {
        const p = pick(this.querySelector('[data-addon-colours]'));
        if (p) {
          const same = items.find((x) => x.id === Number(p.value));
          if (same) same.quantity += 1; else items.push({ id: Number(p.value), quantity: 1 });
        }
      }
      return items;
    }
    update() {
      const r = this.selected;
      if (!r) return;
      this.querySelectorAll('[data-option]').forEach((w) => {
        const on = w.contains(r);
        w.classList.toggle('is-active', on);
        w.querySelector('.dy-bundle').classList.toggle('is-active', on);
      });
      const first = this.querySelector('[data-option]');
      if (this.addon) {
        const onFirst = first.contains(r);
        if (!onFirst) this.addon.checked = false;
        const ac = this.querySelector('[data-addon-colours]');
        if (ac) ac.hidden = !this.addon.checked;
      }
      const addonSaving = this.addon?.checked ? Math.max(0, Number(this.addon.dataset.compare) - Number(this.addon.value)) : 0;
      const saving = Number(r.dataset.compare) - Number(r.dataset.price) + addonSaving;
      this.querySelector('[data-saving-row]').hidden = saving <= 0;
      this.querySelector('[data-saving]').textContent = formatMoney(saving, this.fmt);
    }
    async add() {
      const r = this.selected;
      const btn = this.querySelector('[data-atc]');
      const err = this.querySelector('[data-atc-error]');
      if (!r || btn.disabled) return;
      const items = [{ id: Number(r.value), quantity: Number(r.dataset.qty) || 1 }, ...this.pouchItems()];
      btn.disabled = true; err.hidden = true;
      const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
      let events = null;
      try { events = await import('@shopify/events'); } catch (e) { /* fall back to the cart page */ }
      const deferred = events?.CartLinesUpdateEvent?.createPromise?.();
      if (deferred) {
        this.dispatchEvent(new events.CartLinesUpdateEvent({
          action: 'add', context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: deferred.promise,
        }));
      }
      try {
        const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds.join(',') }),
        }).then((x) => x.json());
        if (res.status) throw new Error(res.description || res.message || 'Could not add to cart');
        const cart = await fetch('/cart.js').then((x) => x.json());
        if (deferred) {
          deferred.resolve({
            cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, source: 'dy-bundles', itemCount: items.reduce((s, i) => s + i.quantity, 0), sections: res.sections, didError: false },
          });
        } else {
          window.location.href = '/cart';
        }
      } catch (e) {
        deferred?.reject?.(e);
        err.textContent = e.message; err.hidden = false;
      } finally {
        btn.disabled = false;
      }
    }
  });
}

if (!customElements.get('dy-before-after')) {
  customElements.define('dy-before-after', class extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type=range]');
      const set = (v) => this.style.setProperty('--pos', `${v}%`);
      range.addEventListener('input', () => set(range.value));
      set(range.value);
    }
  });
}

if (!customElements.get('dy-mini-reviews')) {
  customElements.define('dy-mini-reviews', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.cards = [...this.track.children];
      const dots = this.querySelector('[data-dots]');
      if (this.cards.length < 2) { dots.hidden = true; return; }
      dots.innerHTML = this.cards.map(() => '<span></span>').join('');
      this.dots = [...dots.children];
      this.index = 0;
      this.mark();
      this.track.addEventListener('scroll', () => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (i !== this.index) { this.index = i; this.mark(); }
      }, { passive: true });
      if (!reduced) {
        this.timer = setInterval(() => {
          if (this.matches(':hover')) return;
          const next = (this.index + 1) % this.cards.length;
          this.track.scrollTo({ left: next * this.track.clientWidth, behavior: 'smooth' });
        }, 5000);
      }
    }
    disconnectedCallback() { clearInterval(this.timer); }
    mark() { this.dots.forEach((d, i) => d.classList.toggle('is-on', i === this.index)); }
  });
}
